import React, {useEffect, useRef} from 'react';
import {SVG} from '../../../../../constants/assetLists';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import useScreenSize from '../../../../../hooks/useScreenSize';
import {getCorrectPath, hasPermission} from '../../../../../utils';
import AppTooltip from '../../../../../components/common/AppTooltip';
import ChecklistApproval from '../../Modals/ChecklistApproval';
import {ICONS} from '../../../../users/data';

const colors = ['#ADE6F6', '#FFE797', '#CBCDFF', '#FFABDC'];

function ShiftList() {
  const {setOpenAddCheckListDialog, checklists} = useOperationsContext();
  const [selectedList, setSelectedList] = React.useState<any>(null);
  const clickTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [showApproveChecklist, setShowApproveChecklist] = React.useState<
    string | null
  >(null);

  const screenSize = useScreenSize();
  const isLarge = screenSize == 'xl' || screenSize == '2xl';

  const [expandList, setExpandList] = React.useState(false);

  const handleOpenAddCheckListDialog = () => {
    setOpenAddCheckListDialog(true);
  };

  const handleExpandList = () => {
    setExpandList(!expandList);
  };

  useEffect(() => {
    if (checklists?.checklists?.length == 0) setSelectedList([]);
    else if (!expandList) setSelectedList([]);
  }, [checklists, expandList]);

  const toggleList = (list: any) => {
    if (selectedList?._id === list._id) {
      setSelectedList(null);
    } else {
      setSelectedList(list);
    }
  };

  const handleClick = (shift: any) => {
    clickTimeoutRef.current = setTimeout(() => {
      toggleList(shift);
    }, 200);
  };

  const handleDoubleClick = (list: any) => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    setShowApproveChecklist(list?._id);
  };

  const clickTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleClickItem = (shift: any) => {
    // wait a little to confirm it's not a double click
    clickTimeout.current = setTimeout(() => {
      handleClick(shift);
    }, 250);
  };

  const handleDoubleClickItem = (shift: any) => {
    // double click detected → cancel the single click action
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
    }
    handleDoubleClick(shift); // This opens ChecklistApproval properly
  };

  return (
    <div
      className={`w-full min-h-0 border-t border-t-lightDarkGray ${
        isLarge && 'rounded-bl-[32px] rounded-br-[32px]'
      } px-4 pt-3 pb-4 flex flex-col gap-3 bg-gradient-to-b from-[#EDEDED] to-[#E7EAED]`}>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button className="w-[12px] h-[12px]" onClick={handleExpandList}>
            <img
              src={expandList ? SVG.downBlueIcon : SVG.lessThanBlueIcon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </button>

          <p className="m-0 text-[18px] font-medium text-deepAqua">צ'קליסטים</p>

          <p className="m-0 text-[18px] font-medium text-deepAqua">
            ({checklists?.checklists?.length || 0})
          </p>
        </div>

        {hasPermission('manage_checklists') && (
          <button
            className="p-[10px] rounded-full border-2 border-tealBlue flex items-center justify-center bg-white shadow-md hover:opacity-80 transition-opacity duration-200"
            onClick={handleOpenAddCheckListDialog}>
            <div className="w-[12px] h-[12px]">
              <img
                src={SVG.addBlueIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>
          </button>
        )}
      </div>

      {expandList && (
        <div className="flex-1 min-h-0 flex flex-col gap-2">
          {selectedList?.list?.length > 0 && (
            <div className="max-h-[100px] overflow-y-auto pr-2 pb-2 border-b border-b-lightDarkGray">
              {selectedList?.list?.map(
                (item: {details: string}, index: number) => (
                  <div key={index} className="flex items-start gap-3 mb-1">
                    <span className="text-[8px] mt-[10px]">⚫</span>
                    <span className="text-xl">{item.details}</span>
                  </div>
                ),
              )}
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto flex flex-wrap items-center gap-2">
            {checklists?.checklists?.map((shift: any, index: number) => {
              const bgColor = colors[index % colors.length];
              const departmentIcon = Array.isArray(shift?.departments)
                ? shift?.departments?.[0]?.icon
                : null;
              const icon =
                shift?.departments == 'all'
                  ? SVG.saveAllIcon
                  : departmentIcon
                    ? departmentIcon.startsWith('icon')
                      ? ICONS.find(icon => icon.id === departmentIcon)?.icon
                      : getCorrectPath(departmentIcon, 'image')
                    : null;

              return (
                <AppTooltip title={' לחיצה כפולה לפתיחה'} key={index}>
                  <div
                    key={index}
                    className={`flex items-center gap-2 px-[8px] py-[6px] rounded-[8px] cursor-pointer select-none
                  ${
                    selectedList?._id === shift._id &&
                    'border-1 border-tealBlue'
                  }`}
                    style={{backgroundColor: bgColor}}
                    onClick={() => handleClickItem(shift)}
                    onDoubleClick={() => handleDoubleClickItem(shift)}>
                    {icon && (
                      <img
                        src={icon}
                        alt="icon"
                        className="w-[18px] h-[18px] object-contain"
                      />
                    )}

                    <span className="text-darkerGray">{shift.title}</span>
                  </div>
                </AppTooltip>
              );
            })}
          </div>
        </div>
      )}

      <ChecklistApproval
        checklistId={showApproveChecklist as string}
        open={!!showApproveChecklist}
        onClose={() => setShowApproveChecklist(null)}
      />
    </div>
  );
}

export default ShiftList;
